import { Cached } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useTrackerStartWithSurvey from '../../../hooks/useTrackerStartWithSurvey';
import { TimerContext } from '../context/TimerContext';
import { trackerRunningService } from '../../../services/trackerService';
import { commonLogoutFunc, isLoggedIn } from '../../../utils/share';
import { isSurveyFlowCancelledError } from '../../../utils/surveyGate';
import { PauseButton, PlayButton } from '../constants/icon';
import RunningTaskTime from './RunningTaskTime';
import { useStyles } from '../styles/taskFieldStyles';
import ConfirmBox from './ConfirmBox';
import { updateTask } from '../../../services/taskService';

const TaskField = ({ fetchTaskListData, ...params }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [playLoader, setPlayLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const { startTime, setStartTime, isRunning, selectedTask, setRunning } = useContext(TimerContext);
  const startTrackerWithSurvey = useTrackerStartWithSurvey();

  const handleLogout = () => {
    commonLogoutFunc();
    navigate('/auth/login');
    toast.error('Token expired. Please re-login to complete action');
  };

  const handleStart = async () => {
    setPlayLoader(true);
    const currentTime = new Date().toISOString();
    const body = {
      taskId: selectedTask?.id,
      startTime: currentTime,
      projectId: selectedTask?.projectId,
    };

    try {
      await startTrackerWithSurvey(body);
      setStartTime(body.startTime);
      setRunning(true);
      toast.info('Timer Started');
    } catch (error) {
      if (isSurveyFlowCancelledError(error)) {
        return;
      }

      toast.error(error?.response?.data?.message || 'Error while starting timer');
    } finally {
      setPlayLoader(false);
    }
  };

  const handleOpenPopUp = () => {
    setOpen(true);
  };

  const handleStatusChange = (status) => {
    setNewStatus(status); // Save the new status
  };

  const handleStopTimer = async () => {
    setPlayLoader(true);
    const currentTime = new Date().toISOString();
    try {
      const body = {
        taskId: selectedTask?.id,
        projectId: selectedTask?.projectId,
        stopTime: currentTime,
        startTime,
      };
      await trackerRunningService(body);
      await updateTask(selectedTask?.id, { status: newStatus });
      setRunning(false);
      toast.info('Timer Stopped');

      // Fetch the latest task list data after stopping the timer
      if (fetchTaskListData) {
        await fetchTaskListData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error while stopping timer');
    } finally {
      setPlayLoader(false);
    }
  };

  const handleIconClick = (event) => {
    if (playLoader) {
      event.stopPropagation();
    } else if (!isLoggedIn()) {
      handleLogout();
    } else if (!selectedTask) {
      toast.error('Please select a task!');
    } else if (isRunning) {
      handleOpenPopUp();
    } else {
      handleStart();
    }
  };

  let adornmentIcon;

  if (playLoader) {
    adornmentIcon = <Cached className={classes.rotatingIcon} />;
  } else if (isRunning) {
    adornmentIcon = <img className={classes.icon} src={PauseButton} alt="Pause" />;
  } else {
    adornmentIcon = <img className={classes.icon} src={PlayButton} alt="Play" />;
  }

  return (
    <>
      <TextField
        {...params}
        autoFocus={!isRunning}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <Typography className={classes.timerText}>
                <RunningTaskTime open={!playLoader && isRunning} />
              </Typography>
              <IconButton autoFocus={isRunning} onClick={handleIconClick} disableRipple sx={{ p: 0 }}>
                {adornmentIcon}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <ConfirmBox open={open} setOpen={setOpen} onSave={handleStopTimer} onStatusChange={handleStatusChange} />
    </>
  );
};

export default TaskField;
